import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";

import { Colorize } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string().min(2).max(50).required(),
  color: Yup.string().required(),
  greetingMessage: Yup.string(),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    botId: "",
  };

  const [queue, setQueue] = useState(initialState);
  const [bots, setBots] = useState([]); // ✅ FIX
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  const greetingRef = useRef();

  // 📥 cargar queue
  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue(prev => ({ ...prev, ...data }));
      } catch (err) {
        toastError(err);
      }
    })();

    return () => setQueue(initialState);
  }, [queueId, open]);

  // 📥 cargar bots
  useEffect(() => {
    const fetchBots = async () => {
      try {
        const { data } = await api.get("/bots");
        setBots(data);
      } catch (err) {
        toastError(err);
      }
    };

    fetchBots();
  }, []);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
  };

  const handleSaveQueue = async values => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, values);
      } else {
        await api.post("/queue", values);
      }
      toast.success("Queue saved successfully");
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper" fullWidth maxWidth="sm">
      <DialogTitle>
        {queueId
          ? i18n.t("queueModal.title.edit")
          : i18n.t("queueModal.title.add")}
      </DialogTitle>

      <Formik
        initialValues={queue}
        enableReinitialize
        validationSchema={QueueSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveQueue(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>

              {/* Nombre */}
              <Field
                as={TextField}
                label={i18n.t("queueModal.form.name")}
                name="name"
                fullWidth
                margin="dense"
                variant="outlined"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              {/* Color */}
              <Field
                as={TextField}
                label={i18n.t("queueModal.form.color")}
                name="color"
                margin="dense"
                variant="outlined"
                onFocus={() => {
                  setColorPickerModalOpen(true);
                  greetingRef.current.focus();
                }}
                error={touched.color && Boolean(errors.color)}
                helperText={touched.color && errors.color}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        style={{ backgroundColor: values.color }}
                        className={classes.colorAdorment}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton onClick={() => setColorPickerModalOpen(true)}>
                      <Colorize />
                    </IconButton>
                  ),
                }}
              />

              <ColorPicker
                open={colorPickerModalOpen}
                handleClose={() => setColorPickerModalOpen(false)}
                onChange={color => {
                  setFieldValue("color", color);
                }}
              />

              {/* Greeting */}
              <Field
                as={TextField}
                label={i18n.t("queueModal.form.greetingMessage")}
                name="greetingMessage"
                multiline
                rows={4}
                fullWidth
                margin="dense"
                variant="outlined"
                inputRef={greetingRef}
              />

              {/* 🔥 BOT SELECTOR */}
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Bot</InputLabel>
                <Select
                  value={values.botId || ""}
                  onChange={(e) => setFieldValue("botId", e.target.value)}
                  label="Bot"
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>

                  {bots.map(bot => (
                    <MenuItem key={bot.id} value={bot.id}>
                      {bot.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                variant="outlined"
                disabled={isSubmitting}
              >
                {i18n.t("queueModal.buttons.cancel")}
              </Button>

              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {queueId
                  ? i18n.t("queueModal.buttons.okEdit")
                  : i18n.t("queueModal.buttons.okAdd")}

                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default QueueModal;